import * as React from 'react'
import { PanelDesktop, PanelTriggerDesktop, PanelDropdown, PanelTriggerDropdown } from './Panel'
import { ProfilesDropdown, ProfilesMenu } from '../../Shared/components/ProfilesMenu'
import { RetailNavProps,
  RetailNavState,
  NavigationLogo,
  RetailMenuProps,
  RetailNavDropdownProps,
  PanelHooks,
  NavLink,
  RetailDropdownMenusProps} from '../types'
import { isMoving, reduceMotion, styleForCustomLogo } from '../../utils/logo';
import { Tooltip } from 'DVWebNodeUI'
import { NavigationNewItemNotification, WPSClient, RequestContext } from 'DVWebNodeUtils';
import * as WpsRuntime from 'DVWebNodeWpsRuntime'
import { stringifyOnClient } from 'DVWebNodeUtils'

const wrapWithBentoTooltip = (context: RequestContext, toolTipData: NavigationNewItemNotification) =>
  (wrappedContent: JSX.Element = <></>) => {
  const toolTipContent = <div>
    {toolTipData.title}
    <div>
      {toolTipData.body}
    </div>
  </div>
  const csrfToken = toolTipData.csrfToken

  const onToolipDismiss = async() => {
    await new WPSClient().SetPreferences({
      context,
      csrfToken,
      sessionBased: stringifyOnClient({
        navigationNotificationOptOut: true
      }) as WpsRuntime.atv.wps.SetSessionBasedPreferencesInput
    })
  }

  return (
    <Tooltip
      content={toolTipContent}
      dismissable={true}
      position={'bottom'}
      startOpen={true}
      onDismiss={onToolipDismiss}
      use={'info'}>
      {wrappedContent}
    </Tooltip>
  )
}

const MenuDropdown = ({ links }: NavLink) =>
  <ul className="av-retail-m-nav-panel-menu">
    {links.map((link, index) => (
      <li key={`mobile-dropdown-${index}`}>
        <a href={link.href}
          className={link.isHighlighted
            ? 'av-panel-item-highlight' : ''} rel={link.text}>
          {link.text}
        </a>
      </li>
    ))}
  </ul>


export const RetailDropdown = ({ mainMenuLinks, subMenuLinks } : RetailMenuProps) => {
  const links = mainMenuLinks.concat(subMenuLinks)
  const listItems = links.map((linkProps, index) => {
    const classes = ['av-retail-m-nav-element']
    if (linkProps.isHighlighted) {
      classes.push('av-retail-m-nav-highlighted')
    }
    if (index < mainMenuLinks.length) {
      classes.push('av-retail-m-nav-main-item')
    } else {
      classes.push('av-retail-m-nav-subitem')
      if (index === mainMenuLinks.length) {
        classes.push('av-retail-m-nav-subitem-first')
      } else if (index === links.length - 1) {
        classes.push('av-retail-m-nav-subitem-last')
      }
    }

    if (mainMenuLinks && mainMenuLinks.length && linkProps.isPanel) {
      return (<PanelTriggerDropdown label={linkProps.text}>
        <PanelDropdown lists={mainMenuLinks} />
      </PanelTriggerDropdown>)
    }

    if (linkProps && linkProps.links && linkProps.links.length) {
      return (
      <PanelTriggerDropdown label={linkProps.text}>
        <MenuDropdown {...linkProps}/>
      </PanelTriggerDropdown>)
    }

    return (
      <li className={classes.join(' ')} key={`listitem-${index}`}>
        <a className="av-retail-m-nav-link" href={linkProps.href} rel={linkProps.rel}>
          {linkProps.text}
        </a>
      </li>
    )
  })

  return (
    <ul className="av-retail-m-nav-dropdown">
      {listItems}
    </ul>
  )
}

export const RetailLists = ({ context, mainMenuLinks, subMenuLinks, panelHooks, newItemNotification }: RetailMenuProps) => {
  const mainListItems = mainMenuLinks.map((linkProps, index) => {
    const classes = ['av-retail-m-nav-main-item']
    if (linkProps.isHighlighted) {
      classes.push('av-retail-m-nav-highlighted')
    }

    let subDropdownLinks: JSX.Element[] = null
    if(linkProps.links && linkProps.links.length && !linkProps.isPanel) {
      classes.push('av-retail-m-nav-sbi')
      subDropdownLinks = linkProps.links.map((subLink, index) => (
        <li key={`retaillistitem-${index}`}>
          <a href={subLink.href}>{subLink.text}</a>
        </li>
      ))
    } else if (linkProps.isPanel) {
      return (
        <PanelTriggerDesktop
          label={linkProps.text}
          panelHooks={panelHooks}
          isHighlighted={linkProps.isHighlighted}
        />
      )
    }

    const categoriesDropdown = <>
      <li className={classes.join(' ')} key={`retaillistitem-${index}`}
        onMouseEnter={panelHooks.makeInvisibleMouseEvent}>
        <a href={(!(linkProps.links && linkProps.links.length && !linkProps.isPanel)) &&
          linkProps.href} rel={linkProps.rel}>
          {linkProps.text}
        </a>
        {subDropdownLinks && <ul className="av-retail-nav-sub-dropdown">
          {subDropdownLinks}
        </ul>}
      </li>
      {(subDropdownLinks && newItemNotification) ?
        wrapWithBentoTooltip(context, newItemNotification)() : <></>}
    </>

    return categoriesDropdown
  })

  const sublistItems = subMenuLinks.map((linkProps, index) => {
    const classes = ['av-retail-m-nav-subitem']
    if (linkProps.isHighlighted) {
      classes.push('av-retail-m-nav-highlighted')
    }
    return (
      <li className={classes.join(' ')} key={`sublistitem-${index}`}>
        <a href={linkProps.href} rel={linkProps.rel}>
          {linkProps.text}
        </a>
      </li>
    )
  })

  return (
    <div className="av-retail-m-nav-lists">
      <ul className="av-retail-m-nav-list-main-items">
        {mainListItems}
      </ul>
      <ul className="av-retail-m-nav-list-subitems">
        {sublistItems}
      </ul>
    </div>
  )
}


export class Logo extends React.Component<NavigationLogo, {isReducedMotion: boolean}> {

  constructor(props: NavigationLogo) {
    super(props)
    this.state = {
      isReducedMotion: false
    }
  }

  componentDidMount() {
    const reducedMotionObj = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.setState({'isReducedMotion': reducedMotionObj && reducedMotionObj.matches});
  }

  render() {
    const styledLogo = (this.state.isReducedMotion && this.props.imageURL && isMoving(this.props.imageURL)) ?
      reduceMotion(this.props.imageURL) :
      this.props.imageURL;
    return (
      <a className={styledLogo ? "av-retail-m-nav-custom-logo" : "av-retail-m-nav-text-logo"}
        style={styledLogo ? styleForCustomLogo(styledLogo) : {}}
        href={this.props.href}
       >
        {this.props.altText}
      </a>
    )
  }
}


const Dropdown = ({ context, lists, newItemNotification }: RetailNavDropdownProps) => (
  <>
  <div className="av-retail-m-nav-dropdown-selector">
    <input type="checkbox" className="av-retail-m-nav-toggle-menu"
    name="av-retail-m-nav-menu" id="av-retail-m-nav-toggle-menu"/>
    <label id="av-retail-m-nav-toggle-menu-label"
      htmlFor="av-retail-m-nav-toggle-menu"></label>

    <input type="radio" className="av-retail-m-nav-close-menu"
    name="av-retail-m-nav-menu" id="av-retail-m-nav-close-menu" defaultChecked/>
    <label id="av-retail-m-nav-close-menu-label"
      htmlFor="av-retail-m-nav-close-menu">{ lists.close }</label>

    <input type="radio" className="av-retail-m-nav-open-menu"
      name="av-retail-m-nav-menu" id="av-retail-m-nav-open-menu" />
    <label id="av-retail-m-nav-open-menu-label"
      htmlFor="av-retail-m-nav-open-menu">{ lists.open }</label>

    {newItemNotification &&
      <div className="av-retail-m-nav-dropdown-invisible">
        {wrapWithBentoTooltip(context, newItemNotification)()}
      </div>}

    <div className="av-retail-m-nav-clear"></div>
    <RetailDropdown context={context} {...lists} />
  </div>
  </>
)

export const DropdownMenus = ( { context, lists, isCerberusEnabled, newItemNotification }: RetailDropdownMenusProps) => (
  isCerberusEnabled ? 
    <div className="av-retail-m-nav-dropdown-menus">
      <div className="nav-profiles">
        <ProfilesDropdown {...lists.cerberus} />
        <ol className="nav-profiles-menu">
          <ProfilesMenu {...lists.cerberus} />
        </ol>
      </div>
      <Dropdown context={context} lists={lists} newItemNotification={newItemNotification} />
    </div>
  : <Dropdown context={context} lists={lists} newItemNotification={newItemNotification} />
)

const Navigation = ({ context, logo, lists, panelHooks, isSticky, isCerberusEnabled, isKids, newItemNotification }: RetailNavProps) => (
  <div className={`av-retail-m-nav-container ${isCerberusEnabled ? 'av-retail-m-nav-container-with-profiles' : ''} ${isKids ? 'kids' : ''}`} data-sticky={isSticky}>
    <Logo {...logo} />
    <RetailLists context={context} {...lists} panelHooks={panelHooks} newItemNotification={newItemNotification}/>
    <DropdownMenus context={context} lists={lists} isCerberusEnabled={isCerberusEnabled} newItemNotification={newItemNotification}/>
  </div>
)

export class RetailNavWidget extends React.Component<RetailNavProps, RetailNavState> {
  constructor(props: RetailNavProps) {
    super(props)
    this.state = {
      panelVisible: false
    }
  }

  toggle = () => {
    this.setState({
      panelVisible: !this.state.panelVisible
    })
  }

  makeVisible = () => {
    this.setState({
      panelVisible: true
    })
  }

  makeInvisible = () => {
    this.setState({
      panelVisible: false
    })
  }

  render() {
    let panelHooks: PanelHooks = {
      changeEvent: (_: React.ChangeEvent<HTMLElement>): void => {
        this.toggle()
      },
      makeVisibleMouseEvent: (_: React.MouseEvent<HTMLElement>): void => {
        this.makeVisible()
      },
      makeInvisibleMouseEvent: (_: React.MouseEvent<HTMLElement>): void => {
        this.makeInvisible()
      },
      toggle: this.toggle
    }
    return (
      <React.Fragment>
        <Navigation {...this.props} panelHooks={panelHooks} />
        <PanelDesktop {...this.state}
          panelHooks={panelHooks}
          lists={this.props.lists.mainMenuLinks} />
      </React.Fragment>
    )
  }
}
